import React, { useCallback, useMemo, useState } from 'react';
import {
  buildQueryFilters,
  useGetInfiniteUsers,
  useGetUser,
  GetInfiniteUsersType,
  buildQueryArraySorters,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { CrudFilter } from '@sib/types';
import { AutocompleteIndicator, ComboboxAvataritemOption } from '@sib/ui';
import { useAutocompleteProps } from '@sib/utils';
import { useDebounce } from '@uidotdev/usehooks';
import { getInitials } from '../string-formatters';

export interface UseAssigneeAutocompletePropsOptions {
  organizationId?: string;
  additionalFields?: ComboboxAvataritemOption[];
  excludeCurrentUser?: boolean;
}

export const useAssigneeAutocompleteProps = ({
  organizationId = '',
  additionalFields: externalFields,
  excludeCurrentUser = false,
}: UseAssigneeAutocompletePropsOptions) => {
  const { data: userData, isLoading: isLoadingUser } = useGetUser();
  const { t } = useTranslation();

  const type = organizationId
    ? GetInfiniteUsersType.OrganizationUsers
    : GetInfiniteUsersType.Coworkers;

  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, 500);

  const apiFilter = useMemo<CrudFilter[]>(
    () =>
      debouncedFilter
        ? [
            {
              field: 'name',
              value: debouncedFilter,
              operator: 'contains' as const,
            },
          ]
        : [],
    [debouncedFilter]
  );

  const payload = useMemo(
    () => ({
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      sort: buildQueryArraySorters([{ field: 'name', order: 'asc' as const }]),
      ...(apiFilter.length && { filter: buildQueryFilters(apiFilter) }),
    }),
    [apiFilter]
  );

  const params = useGetInfiniteUsers(type, payload, organizationId);

  const additionalFields = useMemo<ComboboxAvataritemOption[]>(
    () =>
      [
        !excludeCurrentUser &&
          userData && {
            label: (
              <AutocompleteIndicator indicator={t('myself')}>
                {userData?.name}
              </AutocompleteIndicator>
            ),
            value: userData?.id || '',
            initial: getInitials(userData?.name),
            avatarUrl: userData?.avatarUrl,
          },
        ...(externalFields?.filter(i => i.value !== userData?.id) || []),
      ].filter(Boolean) as ComboboxAvataritemOption[],
    [externalFields, userData, excludeCurrentUser, t]
  );

  const optionsMapper = useCallback(
    (user: {
      id: string;
      name: string;
      avatarUrl?: string;
    }): ComboboxAvataritemOption | null => {
      if (excludeCurrentUser && user.id === userData?.id) return null;

      return {
        label: user.name,
        value: user.id,
        initial: getInitials(user.name),
        avatarUrl: user.avatarUrl,
      };
    },
    [excludeCurrentUser, userData?.id]
  );

  const autocompleteResult = useAutocompleteProps({
    params,
    filter,
    setFilter,
    optionsMapper,
    additionalFields,
  });

  return useMemo(
    () => ({
      ...autocompleteResult,
      disabled: isLoadingUser || autocompleteResult.disabled,
      isLoading: isLoadingUser || autocompleteResult.isLoading,
    }),
    [autocompleteResult, isLoadingUser]
  );
};
